import React, { useState, useEffect } from 'react';
import { X, Clock, Sparkles, ArrowRight, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/services/api';

const PromotionalPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [earliestEndDate, setEarliestEndDate] = useState(null);

  useEffect(() => {
    if (sessionStorage.getItem('promo_dismissed')) return;

    const fetchPromotions = async () => {
      try {
        const response = await api.get('/admin/advertisements/active');
        const { advertisements, promotional_plans } = response.data;
        const allPromos = [];
        if (advertisements?.length > 0) allPromos.push(...advertisements);
        if (promotional_plans?.length > 0) {
          promotional_plans.forEach(plan => {
            if (plan.advertisement_config) {
              allPromos.push({
                ...plan.advertisement_config,
                plan_name: plan.name,
                plan_price: plan.billing_type === 'one_time' ? plan.one_time_price : plan.monthly_price,
                billing_type: plan.billing_type,
              });
            }
          });
        }
        // Suppress expired campaigns — a countdown that reads 00:00:00 on
        // every visit destroys trust and violates FTC deceptive-scarcity
        // guidelines. If end_date is present AND in the past, drop the promo.
        const now = new Date();
        const livePromos = allPromos.filter(p => !p.end_date || new Date(p.end_date) > now);
        if (livePromos.length > 0) {
          setPromotions(livePromos);
          const endDates = livePromos.filter(p => p.end_date).map(p => new Date(p.end_date));
          if (endDates.length > 0) setEarliestEndDate(new Date(Math.min(...endDates)));
          setTimeout(() => setIsVisible(true), 1500);
        }
      } catch (error) {
        console.error('Failed to fetch promotions:', error);
      }
    };
    fetchPromotions();
  }, []);

  useEffect(() => {
    if (!earliestEndDate) return;
    const updateCountdown = () => {
      const now = new Date();
      const diff = earliestEndDate - now;
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [earliestEndDate]);

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('promo_dismissed', 'true');
  };

  if (!isVisible || promotions.length === 0) return null;

  // Helper: prepend backend URL if image_url is relative
  const resolveImg = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const primary = promotions[0] || {};
  const heroImage = resolveImg(primary.image_url);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300"
      data-testid="promotional-popup-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) handleDismiss(); }}
    >
      <Card
        className="relative w-full max-w-lg bg-white border border-slate-200 shadow-2xl overflow-hidden rounded-2xl animate-in zoom-in-95 duration-300"
        data-testid="promotional-popup-card"
      >
        {/* IMAGE-BACKGROUND VARIANT (single promo with image) */}
        {heroImage && promotions.length === 1 ? (
          <div
            className="relative min-h-[420px] flex flex-col justify-end"
            style={{
              backgroundImage: `url(${heroImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            data-testid="promo-image-bg"
          >
            {/* Gradient overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/50 to-slate-950/15" aria-hidden="true" />

            {/* Top accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-cyan-400 to-orange-400 z-10" aria-hidden="true" />

            {/* Close */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-white/90 hover:bg-white border border-white/60 transition-colors z-20"
              data-testid="promo-close-button"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-slate-700" />
            </button>

            {/* Content over image */}
            <div className="relative z-10 p-7 pb-7 text-white">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-orange-700 text-xs font-bold tracking-wide mb-3 shadow-sm">
                <Sparkles className="w-3.5 h-3.5" />
                LIMITED TIME OFFER
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight mb-2 drop-shadow-md">
                {primary.headline || 'Special Offer'}
              </h2>
              <p className="text-white/90 mb-4 leading-relaxed drop-shadow">
                {primary.description || "Don't miss this amazing deal"}
              </p>

              {primary.billing_type === 'one_time' && (
                <div className="inline-flex items-center gap-2 mb-4 px-3.5 py-1.5 rounded-xl bg-white/95 text-slate-900">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-bold">Lifetime Access</span>
                  <span className="text-xl font-extrabold ml-1">${primary.plan_price}</span>
                </div>
              )}

              {earliestEndDate && (
                <div className="mb-5">
                  <div className="flex items-center gap-1.5 text-white/80 text-xs mb-2">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="uppercase tracking-wider font-semibold">Offer ends in</span>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { value: countdown.days, label: 'Days' },
                      { value: countdown.hours, label: 'Hrs' },
                      { value: countdown.minutes, label: 'Min' },
                      { value: countdown.seconds, label: 'Sec' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-white/95 backdrop-blur rounded-xl flex items-center justify-center shadow-sm">
                          <span className="text-xl font-extrabold text-slate-900 tabular-nums">{String(item.value).padStart(2, '0')}</span>
                        </div>
                        <span className="text-[10px] text-white/85 mt-1 uppercase tracking-wider font-semibold">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-6 rounded-xl shadow-xl shadow-teal-500/40 transition-all"
                onClick={() => {
                  const ctaUrl = primary.cta_url || '/pricing';
                  window.location.href = ctaUrl;
                }}
                data-testid="promo-cta-button"
              >
                {primary.cta_text || 'Get Started Now'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-center mt-3">
                <button
                  onClick={handleDismiss}
                  className="text-white/70 hover:text-white text-xs font-medium"
                >
                  Maybe later
                </button>
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* TEXT-ONLY / MULTI-PROMO VARIANT */}
            {/* Soft pastel decorations matching site theme */}
            <div className="absolute -top-12 -left-12 w-40 h-40 bg-teal-100/70 rounded-full blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-orange-100/70 rounded-full blur-3xl" aria-hidden="true" />
            {/* Top accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-cyan-400 to-orange-400" aria-hidden="true" />

            {/* Close */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-white/80 hover:bg-slate-100 border border-slate-200 transition-colors z-10"
              data-testid="promo-close-button"
              aria-label="Close promotional popup"
            >
              <X className="w-4 h-4 text-slate-600" />
            </button>

            <CardContent className="relative p-7 pt-9">
              {/* Badge */}
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 border border-orange-200 text-orange-700 text-xs font-semibold tracking-wide">
                  <Sparkles className="w-3.5 h-3.5" />
                  LIMITED TIME OFFER
                </span>
              </div>

              {promotions.length === 1 ? (
                <div className="text-center">
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2 leading-tight">
                    {primary.headline || 'Special Offer'}
                  </h2>
                  <p className="text-slate-600 mb-5">
                    {primary.description || "Don't miss this amazing deal"}
                  </p>
                  {primary.billing_type === 'one_time' && (
                    <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-xl bg-teal-50 border border-teal-100">
                      <Crown className="w-5 h-5 text-amber-500" />
                      <span className="text-teal-700 font-semibold text-sm">Lifetime Access</span>
                      <span className="text-2xl font-extrabold text-slate-900 ml-1">${primary.plan_price}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2 leading-tight">
                    Exclusive Early Bird Offers
                  </h2>
                  <p className="text-slate-600 mb-5">
                    {promotions.length} special deals available now
                  </p>
                  <div className="space-y-2 mb-5">
                    {promotions.slice(0, 3).map((promo, idx) => (
                      <div key={idx} className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                        <span className="text-slate-700 text-sm font-medium">{promo.headline || promo.plan_name}</span>
                        {promo.billing_type === 'one_time' && (
                          <span className="flex items-center gap-1 text-amber-600 text-sm font-bold">
                            <Crown className="w-3.5 h-3.5" /> ${promo.plan_price}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {earliestEndDate && (
                <div className="mb-6">
                  <div className="flex items-center justify-center gap-1.5 text-slate-500 text-xs mb-2.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="uppercase tracking-wider font-semibold">Offer ends in</span>
                  </div>
                  <div className="flex justify-center gap-2">
                    {[
                      { value: countdown.days, label: 'Days' },
                      { value: countdown.hours, label: 'Hrs' },
                      { value: countdown.minutes, label: 'Min' },
                      { value: countdown.seconds, label: 'Sec' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex flex-col items-center">
                        <div className="w-14 h-14 bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-100 rounded-xl flex items-center justify-center shadow-sm">
                          <span className="text-2xl font-extrabold text-slate-900 tabular-nums">{String(item.value).padStart(2, '0')}</span>
                        </div>
                        <span className="text-[10px] text-slate-500 mt-1.5 uppercase tracking-wider font-semibold">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-6 rounded-xl shadow-lg shadow-teal-200/60 transition-all"
                onClick={() => {
                  const ctaUrl = primary.cta_url || '/pricing';
                  window.location.href = ctaUrl;
                }}
                data-testid="promo-cta-button"
              >
                {primary.cta_text || 'Get Started Now'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-center mt-3.5">
                <button
                  onClick={handleDismiss}
                  className="text-slate-500 hover:text-slate-700 text-xs font-medium"
                >
                  Maybe later
                </button>
              </p>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default PromotionalPopup;
